/**
 * Toast + ToastContainer — individual toast view and root-level container.
 *
 * Phone: full-width chip below the header row.
 * Desktop / tablet (≥768): compact card, top-right of the viewport — never a
 * full-width banner across the form column.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §10
 */

import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { View, Text } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { usePathname } from 'expo-router'
import Animated, { FadeInRight, FadeInUp, FadeOut, useReducedMotion } from 'react-native-reanimated'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { runOnJS } from 'react-native-reanimated'
import { Bell, CheckCircle2, XCircle, AlertTriangle, Info, X } from '@/components/ui/icons'
import { Pressable } from './Pressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, Z_INDEX, RADIUS, SPACING, DURATION } from '../../lib/theme/tokens'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import { COLORS, FONTS } from '../../lib/brand'
import { APP_TOP_NAV_HEIGHT, navigationRowBottom } from '../../lib/theme/chrome'
import { shouldShowMobileTopNav } from '../../lib/navigation/mobile-top-nav'
import { COLUMN_PRESETS, DESKTOP_SPACING } from '../../lib/theme/desktop-spacing'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useToast } from '../../lib/hooks/use-toast'
import type { ToastItem, ToastVariant } from '../../lib/toast'

// ─── Variant Config ─────────────────────────────────────────────────────────

function variantIcon(variant: ToastVariant, color: string, size: number) {
  switch (variant) {
    case 'success':
      return <CheckCircle2 size={size} color={color} />
    case 'error':
      return <XCircle size={size} color={color} />
    case 'warning':
      return <AlertTriangle size={size} color={color} />
    case 'info':
      return <Info size={size} color={color} />
    default:
      return <Bell size={size} color={color} />
  }
}

function variantColor(variant: ToastVariant, colors: ReturnType<typeof useSemanticColors>): string {
  switch (variant) {
    case 'success':
      return colors.success
    case 'error':
      return colors.error
    case 'warning':
      return colors.warning
    case 'info':
      return colors.info
    default:
      return colors.textPrimary
  }
}

// ─── Single Toast View ──────────────────────────────────────────────────────

interface ToastViewProps {
  item: ToastItem
  compact: boolean
  onDismiss: (id: string) => void
}

function ToastView({ item, compact, onDismiss }: ToastViewProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const reducedMotion = useReducedMotion()
  const colors = useSemanticColors(isDark)
  const isError = item.variant === 'error'
  const iconColor = isError ? COLORS.white : variantColor(item.variant, colors)
  const backgroundColor = isError
    ? COLORS.coral.DEFAULT
    : isDark
      ? colors.surfaceRaised
      : colors.surface
  const borderColor = isError ? COLORS.coral.DEFAULT : colors.border
  const primaryTextColor = isError ? COLORS.white : colors.textPrimary
  const secondaryTextColor = isError ? COLORS.white : colors.textSecondary
  const closeColor = isError ? COLORS.white : colors.textMuted
  const hasDescription = Boolean(item.description)

  const handleDismiss = useCallback(() => {
    onDismiss(item.id)
  }, [item.id, onDismiss])

  const swipeGesture = Gesture.Pan()
    .activeOffsetX([-20, 20])
    .onEnd((event) => {
      if (Math.abs(event.translationX) > 80) {
        runOnJS(handleDismiss)()
      }
    })

  const entering = reducedMotion
    ? undefined
    : compact
      ? FadeInRight.duration(DURATION.fast)
      : FadeInUp.duration(DURATION.fast).springify()

  return (
    <Animated.View
      collapsable={false}
      entering={entering}
      exiting={reducedMotion ? undefined : FadeOut.duration(DURATION.fast)}
      style={[
        {
          flexDirection: 'row',
          alignItems: hasDescription ? 'flex-start' : 'center',
          paddingLeft: SPACING.md,
          paddingRight: SPACING.xs,
          paddingVertical: isError ? SPACING.sm : SPACING.xs,
          marginHorizontal: compact ? 0 : SPACING.md,
          marginBottom: SPACING.xs,
          borderRadius: RADIUS.md,
          backgroundColor,
          borderWidth: 1,
          borderColor,
          ...WEB_SHADOWS.md,
        },
      ]}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
    >
      <GestureDetector gesture={swipeGesture}>
        <View
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 44,
            flexDirection: 'row',
            alignItems: hasDescription ? 'flex-start' : 'center',
          }}
        >
          <View
            style={{
              width: 24,
              minHeight: 44,
              marginRight: SPACING.sm,
              alignItems: 'center',
              justifyContent: hasDescription ? 'flex-start' : 'center',
              paddingTop: hasDescription ? 2 : 0,
            }}
          >
            {variantIcon(item.variant, iconColor, 20)}
          </View>

          <View style={{ flex: 1, minWidth: 0, minHeight: 44, justifyContent: 'center' }}>
            <Text
              style={{
                fontSize: 14,
                lineHeight: 20,
                fontFamily: FONTS.body,
                color: primaryTextColor,
              }}
              numberOfLines={isError ? 4 : 2}
            >
              {item.message}
            </Text>
            {item.description ? (
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  fontFamily: FONTS.body,
                  color: secondaryTextColor,
                  marginTop: 2,
                }}
                numberOfLines={isError ? 4 : 2}
              >
                {item.description}
              </Text>
            ) : null}
          </View>
        </View>
      </GestureDetector>

      {item.action && (
        <Pressable
          onPress={item.action.onPress}
          style={{
            minWidth: 44,
            minHeight: 44,
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: SPACING.xs,
          }}
          accessibilityRole="button"
          accessibilityLabel={item.action.label}
        >
          <Text
            // No `lineHeight` with `adjustsFontSizeToFit`: iOS shrinks the
            // glyphs to fit the line box and a fixed box does not shrink with
            // them, so the label can blow past any floor. Same defect that
            // made welcome's "Empezar" microscopic.
            style={{
              fontSize: 13,
              fontFamily: FONTS.bodyBold,
              color: isError ? COLORS.white : colors.primary,
            }}
            numberOfLines={1}
            adjustsFontSizeToFit
          >
            {item.action.label}
          </Text>
        </Pressable>
      )}

      <Pressable
        onPress={handleDismiss}
        style={{
          width: 44,
          height: 44,
          alignItems: 'center',
          justifyContent: 'center',
        }}
        accessibilityRole="button"
        accessibilityLabel={t('toast.dismiss')}
        testID="toast.dismiss"
        hitSlop={8}
      >
        <X size={18} color={closeColor} />
      </Pressable>
    </Animated.View>
  )
}

// ─── Toast Container (mounted in RootLayoutApp) ─────────────────────────────

export function ToastContainer() {
  const insets = useSafeAreaInsets()
  const { bp, isDesktop, isTablet, isMobile } = useBreakpoint()
  const pathname = usePathname()
  const { toasts, dismiss } = useToast()

  if (toasts.length === 0) return null

  // Compact top-right from tablet up (≥768). Phone keeps the below-header band.
  const compact = !isMobile
  // The top bar only exists outside desktop, and only on tab roots. `chrome.ts`
  // bakes that into the phone branch so the toast host and the bar cannot
  // disagree about how much chrome is really there.
  const hasMobileTopNav = !isDesktop && shouldShowMobileTopNav(pathname)
  const belowHeader =
    navigationRowBottom({
      insetTop: insets.top,
      isDesktop,
      isTablet,
      hasMobileTopNav,
      desktopShellPaddingY: isDesktop ? DESKTOP_SPACING.shellPaddingY[bp] : 0,
    }) + SPACING.sm

  return (
    <View
      // `pointerEvents` MUST stay a prop here, never a style key.
      //
      // react-native-web only polyfills `box-none`/`box-only` through the
      // atomic-CSS path, which the prop takes (createDOMProps routes it via
      // StyleSheet.create) and which an inline style object does not: RNW's
      // `inline()` compiler documents "No support for ... 'pointerEvents'" and
      // ships the raw value to the DOM, where `box-none` is not a legal CSS
      // value, gets dropped, and the element falls back to `pointer-events:
      // auto`. Native honours either form, so this was invisible on Android and
      // iOS while on app.tago.rent the toast host became a full-width dead band
      // across the top nav for as long as any toast was on screen.
      pointerEvents="box-none"
      testID="toast.host"
      style={{
        position: 'absolute',
        /*
         * Phone: below the header row via `navigationRowBottom` so a persistent
         * error toast cannot sit on the back arrow.
         *
         * Desktop: just under `APP_TOP_NAV_HEIGHT`, pinned to the right. The
         * previous cap (`COLUMN_PRESETS.content` = 800, centered) still painted
         * a fat banner on top of the checkout column. A compact chip in the
         * shell gutter leaves the form and the back control clear.
         */
        top: isDesktop ? APP_TOP_NAV_HEIGHT + SPACING.md : belowHeader,
        ...(compact
          ? {
              right: isDesktop ? DESKTOP_SPACING.shellPaddingX[bp] : SPACING.md,
              width: COLUMN_PRESETS.toast,
              maxWidth: '100%' as const,
              alignItems: 'stretch' as const,
            }
          : {
              left: 0,
              right: 0,
              alignItems: 'center' as const,
            }),
        zIndex: Z_INDEX.toast,
      }}
    >
      {/*
        El tope de ancho va ACA, no en cada aviso.
        En escritorio `COLUMN_PRESETS.toast` (380) es el chip compacto de la
        esquina. El tope no puede vivir en el aviso: el aviso de telefono lleva
        `marginHorizontal`, asi que un `width: '100%'` ahi adentro suma los
        margenes por fuera, se desborda, y se come el pasillo lateral por el
        que tienen que pasar los toques.
      */}
      <View
        pointerEvents="box-none"
        style={{
          width: '100%',
          maxWidth: compact ? COLUMN_PRESETS.toast : undefined,
        }}
      >
        {toasts.map((t) => (
          <ToastView key={t.id} item={t} compact={compact} onDismiss={dismiss} />
        ))}
      </View>
    </View>
  )
}
